import { useState, useEffect, useRef } from 'react'
import { format } from 'date-fns'
import { Plus, X, Upload, ChevronDown } from 'lucide-react'
import { expenseApi, EXPENSE_CATEGORY_LABELS, EXPENSE_STATUS_LABELS, type Expense, type ExpenseCategory, type ExpenseStatus } from '../../api/expenses'
import { Button } from '@/components/ui/button'

const CATEGORY_OPTIONS: ExpenseCategory[] = ['fuel', 'maintenance', 'parking', 'toll', 'other']

const STATUS_COLORS: Record<ExpenseStatus, string> = {
  pending:  'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
}

const CATEGORY_COLORS: Record<ExpenseCategory, string> = {
  fuel:        'bg-blue-100 text-blue-700',
  maintenance: 'bg-purple-100 text-purple-700',
  parking:     'bg-gray-100 text-gray-700',
  toll:        'bg-orange-100 text-orange-700',
  other:       'bg-slate-100 text-slate-700',
}

const today = () => format(new Date(), 'yyyy-MM-dd')

export default function DriverMyExpensesPage() {
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null)

  // form state
  const [category, setCategory] = useState<ExpenseCategory>('fuel')
  const [amount, setAmount] = useState('')
  const [expenseDate, setExpenseDate] = useState(today())
  const [description, setDescription] = useState('')
  const [receiptFile, setReceiptFile] = useState<File | null>(null)
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const fetchExpenses = async () => {
    setLoading(true)
    try {
      const data = await expenseApi.getMyExpenses({ limit: 50 })
      setExpenses(data.items)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchExpenses() }, [])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setReceiptFile(file)
    setReceiptPreview(URL.createObjectURL(file))
  }

  const resetForm = () => {
    setCategory('fuel')
    setAmount('')
    setExpenseDate(today())
    setDescription('')
    setReceiptFile(null)
    setReceiptPreview(null)
    setSubmitError(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!amount || Number(amount) <= 0) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      await expenseApi.createExpense({
        category,
        amount: Number(amount),
        expense_date: new Date(expenseDate).toISOString(),
        description: description || undefined,
        receipt_image: receiptFile || undefined,
      })
      setShowForm(false)
      resetForm()
      fetchExpenses()
    } catch (err: unknown) {
      console.error(err)
      const axiosErr = err as { response?: { data?: { detail?: string } } }
      const msg = axiosErr?.response?.data?.detail || '送出失敗，請稍後再試'
      setSubmitError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('確定刪除此支出？')) return
    try {
      await expenseApi.deleteExpense(id)
      fetchExpenses()
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <div className="p-4 md:p-6 max-w-xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">我的支出</h1>
          <p className="text-sm text-gray-500 mt-0.5">新增與查看個人支出記錄</p>
        </div>
        <Button
          onClick={() => setShowForm(v => !v)}
          className="bg-[#2563EB] hover:bg-blue-700 text-white cursor-pointer"
        >
          <Plus className="w-4 h-4 mr-1" />
          新增支出
        </Button>
      </div>

      {/* New Expense Form */}
      {showForm && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">新增支出</h2>
            <button onClick={() => { setShowForm(false); resetForm() }} className="cursor-pointer text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">類別</label>
              <div className="relative">
                <select
                  value={category}
                  onChange={e => setCategory(e.target.value as ExpenseCategory)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm appearance-none pr-8 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                >
                  {CATEGORY_OPTIONS.map(c => (
                    <option key={c} value={c}>{EXPENSE_CATEGORY_LABELS[c]}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">金額（NT$）</label>
              <input
                type="number"
                min="1"
                step="1"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="請輸入金額"
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">日期</label>
              <input
                type="date"
                value={expenseDate}
                onChange={e => setExpenseDate(e.target.value)}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">備註（選填）</label>
              <input
                type="text"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="補充說明"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Receipt Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">收據照片（選填）</label>
              {receiptPreview ? (
                <div className="relative w-24 h-24">
                  <img src={receiptPreview} alt="preview" className="w-24 h-24 object-cover rounded-lg border" />
                  <button
                    type="button"
                    onClick={() => { setReceiptFile(null); setReceiptPreview(null); if (fileInputRef.current) fileInputRef.current.value = '' }}
                    className="absolute -top-1.5 -right-1.5 bg-white rounded-full border p-0.5 shadow cursor-pointer"
                  >
                    <X className="w-3.5 h-3.5 text-gray-500" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 border border-dashed border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-500 hover:border-blue-400 hover:text-blue-500 transition-colors cursor-pointer"
                >
                  <Upload className="w-4 h-4" />
                  上傳收據圖片
                </button>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#F97316] hover:bg-orange-600 text-white cursor-pointer"
            >
              {submitting ? '送出中...' : '送出支出'}
            </Button>
            {submitError && (
              <p className="text-sm text-red-500 text-center">{submitError}</p>
            )}
          </form>
        </div>
      )}

      {/* Expense List */}
      {loading ? (
        <div className="text-center py-12 text-gray-400 text-sm">載入中...</div>
      ) : expenses.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p className="text-sm">尚無支出記錄</p>
          <p className="text-xs mt-1">點擊「新增支出」開始記錄</p>
        </div>
      ) : (
        <div className="space-y-3">
          {expenses.map(expense => (
            <div key={expense.id} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${CATEGORY_COLORS[expense.category]}`}>
                      {EXPENSE_CATEGORY_LABELS[expense.category]}
                    </span>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[expense.status]}`}>
                      {EXPENSE_STATUS_LABELS[expense.status]}
                    </span>
                  </div>
                  <div className="mt-1.5 text-lg font-bold text-[#F97316]">NT$ {Number(expense.amount).toLocaleString()}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {format(new Date(expense.expense_date), 'yyyy/MM/dd')}
                    {expense.description && ` · ${expense.description}`}
                  </div>
                  {expense.reviewer_note && (
                    <div className="text-xs text-gray-400 mt-1 italic">審核備註：{expense.reviewer_note}</div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {expense.receipt_image_path && (
                    <button onClick={() => setLightboxSrc(expense.receipt_image_path!)} className="cursor-pointer">
                      <img src={expense.receipt_image_path} alt="receipt" className="h-12 w-12 rounded-lg object-cover border hover:opacity-80" />
                    </button>
                  )}
                  {expense.status === 'pending' && (
                    <button
                      onClick={() => handleDelete(expense.id)}
                      className="cursor-pointer text-gray-300 hover:text-red-500 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxSrc(null)}
        >
          <img src={lightboxSrc} alt="receipt" className="max-w-full max-h-full rounded-xl object-contain" />
        </div>
      )}
    </div>
  )
}
